import React, { useState, useEffect } from 'react';
import { X, AlertTriangle } from 'lucide-react';
import {
  Database,
  Plus,
  Edit2,
  Trash2,
  Eye,
  Copy,
  Upload,
  Download,
  Search,
  Filter,
  FileText,
  Table,
  MapPin,
  ChevronDown,
  ChevronRight,
  AlertCircle,
  CheckCircle,
  Calendar,
  User,
  Layers,
  Link,
  BookOpen,
  Globe,
  GlobeLock
} from 'lucide-react';
import templateService from './services/templateService';
import libraryService from './services/libraryService';
import Pagination from './Pagination';
import { logger } from './services/logger';

const TemplateManagementModule = ({
  useAuth,
  usePermissions,
  useNotification,
  useLoading,
  BaseButton,
  BaseInput,
  BaseCard
}) => {
  const { user } = useAuth();
  const { hasPermission } = usePermissions();
  const { addNotification } = useNotification();
  const { showLoading, hideLoading } = useLoading();

  // Estados principales
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [ownerFilter, setOwnerFilter] = useState('my');
  const [showModal, setShowModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [modalType, setModalType] = useState('create'); // create, edit, view, clone
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: '',
    gdbFile: null,
    structure: null,
    status: 'active'
  });
  const [formErrors, setFormErrors] = useState({});
  const [templateToDelete, setTemplateToDelete] = useState(null);

  // Estados para visualización de estructura
  const [expandedTables, setExpandedTables] = useState({});
  const [collapsedSections, setCollapsedSections] = useState({ featureClasses: true, tables: true, domains: true, relationships: true });

  const toggleSection = (section) =>
    setCollapsedSections(prev => ({ ...prev, [section]: !prev[section] }));


  // Estados de la Biblioteca
  const [showLibraryPanel, setShowLibraryPanel] = useState(false);
  const [libraryTemplates, setLibraryTemplates] = useState([]);
  const [libraryLoading, setLibraryLoading] = useState(false);
  const [librarySearchTerm, setLibrarySearchTerm] = useState('');
  const [libraryCategoryFilter, setLibraryCategoryFilter] = useState('all');
  const [libraryCloneTarget, setLibraryCloneTarget] = useState(null);
  const [libraryCloneName, setLibraryCloneName] = useState('');
  const [libraryCloneError, setLibraryCloneError] = useState('');

  // Superadmin del tenant 10S puede gestionar la Biblioteca
  const canManageLibrary = user?.role === 'superadmin' && parseInt(user?.tenantId) === 1;

  // Estados para indicadores de datos mock
  const [isUsingMockData, setIsUsingMockData] = useState(false);

  // Inicialización
  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
      setLoading(true);
      try {
          const data = await templateService.getTemplates();
          setTemplates(data || []);

          // Verificar si estamos usando datos mock
          const usingMock = data && data.length > 0 && data.some(item => item._isMockData);
          setIsUsingMockData(usingMock);

          if (usingMock) {
              addNotification('Mostrando datos de ejemplo (desarrollo)', 'info');
          }
      } catch (error) {
          if (error.response && error.response.status === 401) {
              // Redirigir al usuario al login si la base de datos no está disponible
              window.location.href = '/login';
          } else {
              addNotification('Error al cargar plantillas', 'error');
          }
      } finally {
          setLoading(false);
      }
  };

  const filteredTemplates = templates.filter(template => {
    let matchesOwner = true;
    if (ownerFilter === 'my') {
      matchesOwner = template.createdBy === user?.username;
    } else if (ownerFilter === 'organization') {
      matchesOwner = template.createdBy !== user?.username;
    }

    const matchesSearch = (template.name?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
                         (template.description?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
                         (template.category?.toLowerCase() || '').includes(searchTerm.toLowerCase());

    const matchesStatus = statusFilter === 'all' || template.status === statusFilter;

    return matchesOwner && matchesSearch && matchesStatus;
  });

  // Lógica de paginación
  const itemsPerPage = 5;
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredTemplates.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredTemplates.length / itemsPerPage);

  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(totalPages);
    } else if (currentPage === 0 && totalPages > 0) {
      setCurrentPage(1);
    }
  }, [filteredTemplates, currentPage, totalPages]);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  // Funciones de modal
  const openModal = async (type, template = null) => {
    setModalType(type);

    if (template) {
      setSelectedTemplate(template); // Setear primero con datos básicos disponibles
    } else {
      logger.warn('No template provided to openModal');
      setSelectedTemplate(null);
    }

    setShowModal(true); // Abrir modal inmediatamente

    // Los items de la lista ya incluyen gdbStructure y todos los campos del modal.
    // No se necesita llamar getTemplate(id) — esa llamada cargaba miles de reglas inútilmente.

    // Configurar formData para modales que lo necesitan
    if (template && (type === 'edit' || type === 'clone')) {
      setFormData({
        name: type === 'clone' ? `${template.name} clonado` : template.name,
        description: template.description,
        category: template.category,
        gdbFile: null,
        status: template.status
      });
    } else if (type === 'create') {
      setFormData({
        name: '',
        description: '',
        category: '',
        gdbFile: null,
        status: 'active'
      });
      setFormErrors({});
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedTemplate(null);
    setFormData({
      name: '',
      description: '',
      category: '',
      gdbFile: null,
      status: 'active'
    });
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'El nombre de la plantilla es requerido.';
    if (!formData.category) newErrors.category = 'Debe seleccionar una categoría.';
    if (modalType === 'create' && !formData.gdbFile) {
      newErrors.gdbFile = 'Debe seleccionar un archivo GeoDatabase comprimido (.zip) para crear la plantilla.';
    }

    // Validate unique name for create and clone
    if (modalType === 'create' || modalType === 'clone') {
      const existingTemplate = templates.find(t =>
        t.name.toLowerCase() === formData.name.toLowerCase().trim() &&
        (modalType === 'create' || t.id !== selectedTemplate?.id)
      );
      if (existingTemplate) {
        newErrors.name = 'Ya existe una plantilla con este nombre.';
      }
    }

    setFormErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      // Mostrar error específico si existe
      if (formErrors.name) {
        addNotification(formErrors.name, 'error');
      } else if (formErrors.category) {
        addNotification(formErrors.category, 'error');
      } else if (formErrors.gdbFile) {
        addNotification(formErrors.gdbFile, 'error');
      } else {
        addNotification('Por favor complete todos los campos requeridos.', 'error');
      }
      return;
    }

    setLoading(true);
    const loadingMessage = modalType === 'create' ? 'Creando plantilla y procesando archivo GDB...' :
                          modalType === 'edit' ? 'Actualizando plantilla...' :
                          modalType === 'clone' ? 'Clonando plantilla...' : 'Guardando...';
    showLoading(loadingMessage);

    try {
      let savedTemplate;
      if (modalType === 'create') {
        savedTemplate = await templateService.createTemplate(formData);
        addNotification('Plantilla creada exitosamente', 'success');
      } else if (modalType === 'clone') {
        savedTemplate = await templateService.cloneTemplate(selectedTemplate.id, {
          name: formData.name,
          description: formData.description,
          category: formData.category
        });
        addNotification(`Plantilla "${formData.name}" clonada exitosamente`, 'success');
      } else if (modalType === 'edit') {
        const updateData = {
          name: formData.name,
          description: formData.description,
          category: formData.category,
          status: formData.status,
        };
        savedTemplate = await templateService.updateTemplate(selectedTemplate.id, updateData);
        addNotification('Plantilla actualizada exitosamente', 'success');
      }

      // Recargar la lista de plantillas después de cualquier operación exitosa
      await loadTemplates();
      closeModal();
    } catch (error) {
      const msg = error.message || 'Error al guardar plantilla';
      // Si el error es de nombre duplicado, mostrarlo inline en el campo nombre
      if (msg.includes('Ya existe una plantilla') || msg.includes('nombre')) {
        setFormErrors(prev => ({ ...prev, name: msg }));
        addNotification(msg, 'error');
      } else {
        addNotification(msg, 'error');
      }
      logger.error('Error en handleSave:', { error });
    } finally {
      setLoading(false);
      hideLoading();
    }  };

  const handleClone = async (template) => {
    showLoading('Preparando clonación de plantilla...');
    try {
      await openModal('clone', template);
    } finally {
      hideLoading();
    }
  };

  const handleDelete = (template) => {
    if (!hasPermission('delete_templates')) {
        addNotification('No tienes permisos para eliminar plantillas.', 'error');
        return;
    }
    setTemplateToDelete(template);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!templateToDelete) return;

    try {
      setLoading(true);
      showLoading('Eliminando plantilla...');
      // Pasar el usuario actual como deactivatedBy
      await templateService.deleteTemplate(templateToDelete.id, {
        deactivatedBy: user?.username || 'system'
      });
      addNotification(`Plantilla "${templateToDelete.name}" eliminada exitosamente`, 'success');

      setShowDeleteModal(false);
      setTemplateToDelete(null);
      // Recargar la lista de plantillas después de eliminar
      await loadTemplates();
    } catch (error) {
      addNotification(error.message || 'Error al eliminar plantilla', 'error');
    } finally {
      setLoading(false);
      hideLoading();
    }
  };

  const openLibraryPanel = async () => {
    setShowLibraryPanel(true);
    setLibrarySearchTerm('');
    setLibraryCategoryFilter('all');
    setLibraryCloneTarget(null);
    if (libraryTemplates.length === 0) {
      await loadLibraryTemplates();
    }
  };

  const loadLibraryTemplates = async () => {
    setLibraryLoading(true);
    try {
      const data = await libraryService.getLibraryTemplates();
      setLibraryTemplates(data || []);
    } catch (error) {
      addNotification('Error al cargar la Biblioteca', 'error');
    } finally {
      setLibraryLoading(false);
    }
  };

  const handleCloneFromLibrary = (template) => {
    setLibraryCloneTarget(template);
    setLibraryCloneName(`${template.name} (copia)`);
    setLibraryCloneError('');
  };

  const confirmCloneFromLibrary = async () => {
    if (!libraryCloneName.trim()) {
      setLibraryCloneError('El nombre es requerido.');
      return;
    }
    const exists = templates.find(t => t.name.toLowerCase() === libraryCloneName.trim().toLowerCase());
    if (exists) {
      setLibraryCloneError('Ya tienes una plantilla con ese nombre.');
      return;
    }
    showLoading('Clonando plantilla de la Biblioteca...');
    try {
      await libraryService.cloneFromLibrary(libraryCloneTarget.id, { name: libraryCloneName.trim(), description: libraryCloneTarget.description });
      addNotification(`Plantilla "${libraryCloneName.trim()}" clonada exitosamente desde la Biblioteca`, 'success');
      setLibraryCloneTarget(null);
      setLibraryCloneName('');
      await loadTemplates();
    } catch (error) {
      const msg = error.message || 'Error al clonar plantilla';
      if (msg.toLowerCase().includes('nombre') || msg.toLowerCase().includes('existe')) {
        setLibraryCloneError(msg);
      } else {
        addNotification(msg, 'error');
      }
    } finally {
      hideLoading();
    }
  };

  const handleToggleLibrary = async (template) => {
    const action = template.isLibrary ? 'retirar de' : 'publicar en';
    showLoading(`Actualizando Biblioteca...`);
    try {
      if (template.isLibrary) {
        await libraryService.unpublishFromLibrary(template.id);
        addNotification(`Plantilla retirada de la Biblioteca`, 'success');
      } else {
        await libraryService.publishToLibrary(template.id);
        addNotification(`Plantilla publicada en la Biblioteca`, 'success');
      }
      await loadTemplates();
      // Refrescar lista de biblioteca si el panel está abierto
      if (showLibraryPanel) await loadLibraryTemplates();
    } catch (error) {
      addNotification(error.message || `Error al ${action} la Biblioteca`, 'error');
    } finally {
      hideLoading();
    }
  };

  const handleFile = (file) => {
    if (file) {
      const fileName = file.name.toLowerCase();
      if (!fileName.endsWith('.zip')) {
        addNotification('Por favor seleccione un archivo .zip válido', 'error');
        setFormData(prev => ({ ...prev, gdbFile: null }));
        setFormErrors(prev => ({ ...prev, gdbFile: 'Debe seleccionar un archivo GeoDatabase comprimido (.zip) para crear la plantilla.' }));
        return;
      }

      setFormData(prev => ({ ...prev, gdbFile: file }));
      // Limpiar el error específico del archivo al seleccionarlo correctamente
      setFormErrors(prev => ({ ...prev, gdbFile: undefined }));
    }
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    handleFile(file);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setIsDragging(false);
    const file = event.dataTransfer.files[0];
    handleFile(file);
  };

  // Función para expandir/contraer tablas en vista de estructura
  const toggleTableExpansion = (tableName) => {
    setExpandedTables(prev => ({
      ...prev,
      [tableName]: !prev[tableName]
    }));
  };

  // Componente para mostrar estructura de GDB
  const StructureViewer = ({ structure, readOnly = false }) => {
    if (!structure) return <p className="text-gray-500 text-center py-8">No hay estructura disponible</p>;

    return (
      <div className="space-y-6">
        {/* Feature Classes */}
        {structure.featureClasses && structure.featureClasses.length > 0 && (
          <div>
            <h4
              className="font-semibold text-gray-900 mb-3 flex items-center cursor-pointer select-none hover:text-primary"
              onClick={() => toggleSection('featureClasses')}
            >
              {collapsedSections.featureClasses ? <ChevronRight className="mr-1" size={16} /> : <ChevronDown className="mr-1" size={16} />}
              <Layers className="mr-2" size={18} />
              Feature Classes ({structure.featureClasses.length})
            </h4>
            {!collapsedSections.featureClasses && <div className="space-y-3">
              {structure.featureClasses.map((fc, index) => (
                <div key={index} className="border border-gray-200 rounded-lg">
                  <div 
                    className="p-3 bg-gray-50 cursor-pointer flex items-center justify-between"
                    onClick={() => toggleTableExpansion(`fc_${index}`)}
                  >
                    <div className="flex items-center space-x-3">
                      {expandedTables[`fc_${index}`] ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                      <MapPin className="text-blue-600" size={16} />
                      <span className="font-medium">{fc.name}</span>
                      <span className="text-sm text-gray-500">({fc.type})</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                        {fc.fields?.length || 0} campos
                      </span>
                      {fc.validations && fc.validations.length > 0 && (
                        <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">
                          {fc.validations.length} validaciones
                        </span>
                      )}
                    </div>
                  </div>
                  
                  {expandedTables[`fc_${index}`] && (
                    <div className="p-3 border-t border-gray-200">
                      {fc.spatialReferenceInfo && (
                        <div className="mb-3 p-2 bg-blue-50 border border-blue-200 rounded text-sm">
                          <span className="font-medium text-blue-800">CRS: </span>
                          {fc.spatialReferenceInfo.canonicalName || fc.spatialReferenceInfo.CanonicalName
                            ? <>
                                <span className="text-blue-700 font-medium">{fc.spatialReferenceInfo.canonicalName || fc.spatialReferenceInfo.CanonicalName}</span>
                                {(fc.spatialReferenceInfo.crsName || fc.spatialReferenceInfo.CrsName) && (
                                  <span className="text-gray-600 ml-2">({fc.spatialReferenceInfo.crsName || fc.spatialReferenceInfo.CrsName})</span>
                                )}
                              </>
                            : (fc.spatialReferenceInfo.crsName || fc.spatialReferenceInfo.CrsName)
                              ? <span className="text-blue-700 font-medium">{fc.spatialReferenceInfo.crsName || fc.spatialReferenceInfo.CrsName}</span>
                              : <span className="text-gray-400 italic">Sin referencia espacial identificada</span>
                          }
                          {(fc.spatialReferenceInfo.wkt || fc.spatialReferenceInfo.Wkt) && (() => {
                            const wktFull = fc.spatialReferenceInfo.wkt || fc.spatialReferenceInfo.Wkt;
                            const wktShort = wktFull.length > 20 ? wktFull.substring(0, 20) + '...' : wktFull;
                            return (
                              <span
                                className="text-gray-500 ml-2 cursor-pointer hover:text-blue-600"
                                title="Clic para copiar WKT completo"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigator.clipboard.writeText(wktFull);
                                  const el = e.currentTarget;
                                  const orig = el.textContent;
                                  el.textContent = '✓ Copiado';
                                  setTimeout(() => { el.textContent = orig; }, 1500);
                                }}
                              >
                                WKT: {wktShort}
                              </span>
                            );
                          })()}
                        </div>
                      )}
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b border-gray-200">
                              <th className="text-left py-2 px-3">Campo</th>
                              <th className="text-left py-2 px-3">Tipo</th>
                              <th className="text-left py-2 px-3">Longitud</th>
                              <th className="text-left py-2 px-3">Requerido</th>
                            </tr>
                          </thead>
                          <tbody>
                            {fc.fields?.map((field, fieldIndex) => (
                              <tr key={fieldIndex} className="border-b border-gray-100">
                                <td className="py-2 px-3 font-medium">{field.name}</td>
                                <td className="py-2 px-3">{field.type}</td>
                                <td className="py-2 px-3">{field.length || '-'}</td>
                                <td className="py-2 px-3">
                                  {field.required ? (
                                    <CheckCircle className="text-green-600" size={16} />
                                  ) : (
                                    <span className="text-gray-400">-</span>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>}
          </div>
        )}

        {/* Tables */}
        {structure.tables && structure.tables.length > 0 && (
          <div>
            <h4
              className="font-semibold text-gray-900 mb-3 flex items-center cursor-pointer select-none hover:text-primary"
              onClick={() => toggleSection('tables')}
            >
              {collapsedSections.tables ? <ChevronRight className="mr-1" size={16} /> : <ChevronDown className="mr-1" size={16} />}
              <Table className="mr-2" size={18} />
              Tablas ({structure.tables.length})
            </h4>
            {!collapsedSections.tables && <div className="space-y-3">
              {structure.tables.map((table, index) => (
                <div key={index} className="border border-gray-200 rounded-lg">
                  <div 
                    className="p-3 bg-gray-50 cursor-pointer flex items-center justify-between"
                    onClick={() => toggleTableExpansion(`table_${index}`)}
                  >
                    <div className="flex items-center space-x-3">
                      {expandedTables[`table_${index}`] ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                      <Table className="text-purple-600" size={16} />
                      <span className="font-medium">{table.name}</span>
                    </div>
                    <span className="text-xs bg-purple-100 text-purple-800 px-2 py-1 rounded">
                      {table.fields?.length || 0} campos
                    </span>
                  </div>
                  
                  {expandedTables[`table_${index}`] && (
                    <div className="p-3 border-t border-gray-200">
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b border-gray-200">
                              <th className="text-left py-2 px-3">Campo</th>
                              <th className="text-left py-2 px-3">Tipo</th>
                              <th className="text-left py-2 px-3">Longitud</th>
                              <th className="text-left py-2 px-3">Requerido</th>
                            </tr>
                          </thead>
                          <tbody>
                            {table.fields?.map((field, fieldIndex) => (
                              <tr key={fieldIndex} className="border-b border-gray-100">
                                <td className="py-2 px-3 font-medium">{field.name}</td>
                                <td className="py-2 px-3">{field.type}</td>
                                <td className="py-2 px-3">{field.length || '-'}</td>
                                <td className="py-2 px-3">
                                  {field.required ? (
                                    <CheckCircle className="text-green-600" size={16} />
                                  ) : (
                                    <span className="text-gray-400">-</span>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>}
          </div>
        )}

        {/* Dominios */}
        {structure.domains && structure.domains.length > 0 && (
          <div>
            <h4
              className="font-semibold text-gray-900 mb-3 flex items-center cursor-pointer select-none hover:text-primary"
              onClick={() => toggleSection('domains')}
            >
              {collapsedSections.domains ? <ChevronRight className="mr-1" size={16} /> : <ChevronDown className="mr-1" size={16} />}
              Dominios ({structure.domains.length})
            </h4>
            {!collapsedSections.domains && <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {structure.domains.map((domain, index) => (
                <div key={index} className="p-3 border border-gray-200 rounded-lg">
                  <div className="font-medium">{domain.name}</div>
                  <div className="text-sm text-gray-600">Tipo: {domain.type}</div>
                  {domain.type === 'coded' && domain.values && domain.values.length > 0 && (
                    <div className="text-sm text-gray-600 mt-1">
                      Valores: {domain.values.join(', ')}
                    </div>
                  )}
                  {domain.type === 'range' && domain.minValue != null && domain.maxValue != null && (
                    <div className="text-sm text-gray-600 mt-1">
                      Rango: min={domain.minValue}, max={domain.maxValue}
                    </div>
                  )}
                </div>
              ))}
            </div>}
          </div>
        )}

        {/* Relaciones */}
        {structure.relationships && structure.relationships.length > 0 && (
          <div>
            <h4
              className="font-semibold text-gray-900 mb-3 flex items-center cursor-pointer select-none hover:text-primary"
              onClick={() => toggleSection('relationships')}
            >
              {collapsedSections.relationships ? <ChevronRight className="mr-1" size={16} /> : <ChevronDown className="mr-1" size={16} />}
              <Link className="mr-2" size={18} />
              Relaciones ({structure.relationships.length})
            </h4>
            {!collapsedSections.relationships && (
              <div className="space-y-2">
                {structure.relationships.map((rel, index) => {
                  const originTable = rel.OriginTable || rel.originTable || '';
                  const originKey   = rel.OriginPrimaryKey || rel.originPrimaryKey || '';
                  const destTable   = rel.DestinationTable || rel.destinationTable || '';
                  const destKey     = rel.DestinationForeignKey || rel.destinationForeignKey || '';
                  const cardinality = rel.Cardinality || rel.cardinality || '';
                  const cardLabel   = cardinality.includes('OneToMany') ? '1:N'
                                    : cardinality.includes('OneToOne')  ? '1:1'
                                    : cardinality.includes('ManyToMany') ? 'N:N'
                                    : cardinality;
                  const name = rel.Name || rel.name || '';
                  return (
                    <div key={index} className="p-3 border border-gray-200 rounded-lg flex items-center gap-3">
                      <div className="flex-1 font-mono text-sm">
                        <span className="font-semibold text-blue-700">{originTable}</span>
                        {originKey && <span className="text-gray-500">.{originKey}</span>}
                        <span className="mx-2 text-gray-400">→</span>
                        <span className="font-semibold text-purple-700">{destTable}</span>
                        {destKey && <span className="text-gray-500">.{destKey}</span>}
                      </div>
                      {cardLabel && (
                        <span className="text-xs bg-primary/10 text-primary-text px-2 py-1 rounded font-semibold flex-shrink-0">
                          {cardLabel}
                        </span>
                      )}
                      {name && (
                        <span className="text-xs text-gray-400 flex-shrink-0 truncate max-w-[140px]" title={name}>
                          {name}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':   return 'bg-green-100 text-green-800';
      case 'inactive': return 'bg-gray-100 text-gray-800';
      default:         return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'active':   return 'Activa';
      case 'inactive': return 'Inactiva';
      default:         return status || 'Sin estado';
    }
  };

  return (
    <div className="space-y-6">
      {/* Indicador de datos mock */}
      {isUsingMockData && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center">
            <AlertCircle className="text-blue-600 mr-2" size={20} />
            <div className="flex-1">
              <h3 className="text-sm font-medium text-blue-800">Modo Desarrollo - Datos de Ejemplo</h3>
              <p className="text-sm text-blue-600 mt-1">
                Se están mostrando datos de ejemplo porque el backend no está disponible.
                Los cambios realizados no se persistirán.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Header con controles */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Gestión de Plantillas</h2>
          <p className="text-gray-600">Administre las plantillas de estructura GeoDatabase</p>
        </div>
        
        <div className="flex items-center gap-3">
          <BaseButton
            variant="secondary"
            onClick={openLibraryPanel}
            icon={BookOpen}
          >
            Explorar Biblioteca
          </BaseButton>
          {hasPermission('create_templates') && (
            <BaseButton
              onClick={() => openModal('create')}
              icon={Plus}
            >
              Nueva Plantilla
            </BaseButton>
          )}
        </div>
      </div>

      {/* Filtros y búsqueda */}
      <BaseCard>
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <BaseInput
              placeholder="Buscar plantillas..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              icon={Search}
            />
          </div>
          
          <div className="md:w-48">
            <select
              value={ownerFilter}
              onChange={e => setOwnerFilter(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="my">Mis plantillas</option>
              {/* <option value="organization">Plantillas organización</option> */}
              <option value="all">Todas las plantillas</option>
            </select>
          </div>

          <div className="md:w-48">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">Todos los estados</option>
              <option value="active">Activas</option>
              <option value="inactive">Inactivas</option>
            </select>
          </div>

        </div>
      </BaseCard>

      {/* Lista de plantillas */}
      <BaseCard>
        {loading ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : filteredTemplates.length === 0 ? (
          <div className="text-center py-8">
            <Database className="mx-auto text-gray-400 mb-4" size={48} />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No hay plantillas</h3>
            <p className="text-gray-600">
              {searchTerm || statusFilter !== 'all' 
                ? 'No hay plantillas que coincidan con los filtros aplicados.'
                : 'Cree su primera plantilla para comenzar.'
              }
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-4">Nombre</th>
                  <th className="text-left py-3 px-4">Categoría</th>
                  <th className="text-left py-3 px-4">Estado</th>
                  <th className="text-left py-3 px-4">Creado por</th>
                  <th className="text-left py-3 px-4">Fecha de creación</th>
                  <th className="text-left py-3 px-4">Última modificación</th>
                  <th className="text-right py-3 px-4">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {currentItems.map((template) => (
                  <tr key={template.id} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-3 px-4">
                      <div>
                        <div className="font-medium text-gray-900">{template.name}</div>
                        <div className="text-sm text-gray-500">{template.description}</div>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        {template.category}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(template.status)}`}>
                        {getStatusText(template.status)}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center">
                        <User className="mr-2 text-gray-400" size={16} />
                        <span className="text-sm text-gray-900">{template.createdBy}</span>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center text-sm text-gray-500">
                        <Calendar className="mr-2" size={16} />
                        {template.createdAt ? new Date(template.createdAt).toLocaleString('es-ES') : 'N/A'}
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center text-sm text-gray-500">
                        <Calendar className="mr-2" size={16} />
                        {template.lastModified}
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center justify-end space-x-2">
                        <button
                          onClick={() => openModal('view', template)}
                          className="p-1 text-gray-600 hover:text-blue-600 transition-colors"
                          title="Ver detalles"
                        >
                          <Eye size={16} />
                        </button>
                        
                        {hasPermission('edit_templates') && (user?.role === 'admin' || template.createdBy === user?.username) && (
                          <button
                            onClick={() => openModal('edit', template)}
                            className="p-1 text-gray-600 hover:text-indigo-600 transition-colors"
                            title="Editar"
                          >
                            <Edit2 size={16} />
                          </button>
                        )}
                        
                        {hasPermission('clone_templates') && (
                          <button
                            onClick={() => handleClone(template)}
                            className="p-1 text-gray-600 hover:text-green-600 transition-colors"
                            title="Clonar"
                          >
                            <Copy size={16} />
                          </button>
                        )}
                        
                        {canManageLibrary && (
                          <button
                            onClick={(e) => { e.stopPropagation(); handleToggleLibrary(template); }}
                            className={`p-1 transition-colors ${template.isLibrary ? 'text-indigo-600 hover:text-gray-500' : 'text-gray-400 hover:text-indigo-600'}`}
                            title={template.isLibrary ? 'Retirar de la Biblioteca' : 'Publicar en la Biblioteca'}
                          >
                            <Globe size={16} />
                          </button>
                        )}

                        {hasPermission('delete_templates') && (user?.role === 'admin' || template.createdBy === user?.username) && (
                          <button
                            onClick={(e) => { e.stopPropagation(); handleDelete(template); }}
                            className="p-1 text-gray-600 hover:text-red-600 transition-colors"
                            title="Eliminar"
                          >
                            <Trash2 size={16} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={paginate}
          totalItems={filteredTemplates.length}
          itemsPerPage={itemsPerPage}
          BaseButton={BaseButton}
        />
      </BaseCard>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">
                  {modalType === 'create' && 'Nueva Plantilla'}
                  {modalType === 'edit' && 'Editar Plantilla'}
                  {modalType === 'view' && 'Ver Plantilla'}
                  {modalType === 'clone' && 'Clonar Plantilla'}
                </h3>
                <button
                  onClick={closeModal}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X size={24} />
                </button>
              </div>
            </div>

            <div className="p-6">
              {modalType === 'view' ? (
                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Nombre</label>
                      <p className="text-gray-900">{selectedTemplate?.name}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Categoría</label>
                      <p className="text-gray-900">{selectedTemplate?.category}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Estado</label>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(selectedTemplate?.status)}`}>
                        {getStatusText(selectedTemplate?.status)}
                      </span>
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Descripción</label>
                      <p className="text-gray-900">{selectedTemplate?.description}</p>
					  </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">Estructura de la GeoDatabase</label>
                    <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                      {(() => {
                        try {
                          // Verificar si gdbStructure existe
                          if (!selectedTemplate?.gdbStructure) {
                            return (
                              <div className="text-center py-8">
                                <AlertCircle className="mx-auto text-yellow-400 mb-4" size={48} />
                                <h3 className="text-lg font-medium text-gray-900 mb-2">Estructura no disponible</h3>
                                <p className="text-gray-600">Esta plantilla no tiene estructura GDB procesada</p>
                                <p className="text-sm text-gray-500 mt-2">La estructura se genera al subir un archivo .zip con el GeoDatabase</p>
                              </div>
                            );
                          }

                          let structure;

                          // Intentar parsear JSON si es string
                          if (typeof selectedTemplate.gdbStructure === 'string') {
                            try {
                              structure = JSON.parse(selectedTemplate.gdbStructure);
                            } catch (jsonError) {
                              // Intentar limpiar el JSON si tiene caracteres problemáticos
                              try {
                                const cleanedJson = selectedTemplate.gdbStructure
                                  .replace(/[\u0000-\u001F\u007F-\u009F]/g, '') // Remover caracteres de control
                                  .replace(/\\n/g, '\\\\n') // Escapar saltos de línea
                                  .replace(/\\r/g, '\\\\r') // Escapar retornos de carro
                                  .replace(/\\t/g, '\\\\t'); // Escapar tabs

                                structure = JSON.parse(cleanedJson);
                              } catch (cleanError) {
                                throw new Error(`JSON malformado: ${jsonError.message}`);
                              }
                            }
                          } else {
                            structure = selectedTemplate.gdbStructure;
                          }

                          // Verificar que la estructura tenga contenido
                          if (!structure || (structure.tables?.length === 0 && structure.featureClasses?.length === 0)) {
                            return (
                              <div className="text-center py-8">
                                <AlertCircle className="mx-auto text-yellow-400 mb-4" size={48} />
                                <h3 className="text-lg font-medium text-gray-900 mb-2">Estructura vacía</h3>
                                <p className="text-gray-600">La estructura GDB está vacía o no contiene tablas/feature classes</p>
                              </div>
                            );
                          }

                          // Normalize property names to lowercase for compatibility
                          structure = {
                            featureClasses: (structure.FeatureClasses || structure.featureClasses || []).map(fc => ({
                              name: fc.Name || fc.name,
                              type: fc.Type || fc.type,
                              spatialReferenceInfo: fc.SpatialReferenceInfo || fc.spatialReferenceInfo || null,
                              fields: (fc.Fields || fc.fields || []).map(field => ({
                                name: field.Name || field.name,
                                type: field.Type || field.type,
                                length: field.Length || field.length,
                                required: field.Required || field.required
                              })),
                              validations: fc.Validations || fc.validations || []
                            })),
                            tables: (structure.Tables || structure.tables || []).map(table => ({
                              name: table.Name || table.name,
                              fields: (table.Fields || table.fields || []).map(field => ({
                                name: field.Name || field.name,
                                type: field.Type || field.type,
                                length: field.Length || field.length,
                                required: field.Required || field.required
                              })),
                              validations: table.Validations || table.validations || []
                            })),
                            domains: structure.Domains || structure.domains || [],
                            relationships: structure.Relationships || structure.relationships || []
                          };

                          return <StructureViewer structure={structure} readOnly={true} />;
                        } catch (error) {
                          logger.error('Error parsing GDB structure:', { error: error.message });

                          return (
                            <div className="text-center py-8">
                              <AlertCircle className="mx-auto text-red-400 mb-4" size={48} />
                              <h3 className="text-lg font-medium text-gray-900 mb-2">Error al cargar estructura</h3>
                              <p className="text-gray-600">No se pudo parsear la estructura de la GeoDatabase</p>
                              {process.env.NODE_ENV === 'development' && (
                                <details className="mt-4">
                                  <summary className="cursor-pointer text-sm text-gray-500">Ver detalles del error</summary>
                                  <div className="mt-2 text-xs bg-red-50 p-2 rounded">
                                    <strong>Error:</strong> {error.message}
                                  </div>
                                </details>
                              )}
                            </div>
                          );
                        }
                      })()}
                    </div>
                  </div>

                  {hasPermission('edit_templates') && (user?.role === 'admin' || selectedTemplate?.createdBy === user?.username) && (
                    <div className="flex justify-end pt-4 border-t border-gray-200">
                      <BaseButton
                        onClick={() => {
                          setModalType('edit');
                          setFormData({
                            name: selectedTemplate?.name || '',
                            description: selectedTemplate?.description || '',
                            category: selectedTemplate?.category || '',
                            gdbFile: null,
                            status: selectedTemplate?.status || 'active'
                          });
                        }}
                      >
                        <Edit2 size={16} className="mr-2" />
                        Editar Plantilla
                      </BaseButton>
                    </div>
                  )}
                </div>
              ) : (
                <form onSubmit={(e) => { e.preventDefault(); handleSave(); }} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Nombre *
                      </label>
                      <BaseInput
                        value={formData.name}
                        onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                        placeholder="Nombre de la plantilla"
                      />
                      {formErrors.name && (
                        <p className="mt-1 text-sm text-red-600">{formErrors.name}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Categoría *
                      </label>
                      <select
                        value={formData.category}
                        onChange={(e) => {
                          logger.debug('Category select onChange', { value: e.target.value });
                          setFormData(prev => ({ ...prev, category: e.target.value }));
                        }}
                        onFocus={() => setFormErrors(prev => ({ ...prev, category: '' }))} // Clear error on focus
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="">Seleccione una categoría</option>
                        <option value="Catastro">Catastro</option>
                        <option value="Infraestructura">Infraestructura</option>
                        <option value="Medio Ambiente">Medio Ambiente</option>
                        <option value="Servicios Públicos">Servicios Públicos</option>
                        <option value="Planificación Urbana">Planificación Urbana</option>
                        <option value="Transporte">Transporte</option>
                        <option value="Otro">Otro</option>
                      </select>
                      {formErrors.category && (
                        <p className="mt-1 text-sm text-red-600">{formErrors.category}</p>
                      )}
                    </div>

                    {(modalType === 'create' || modalType === 'edit') && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Estado
                        </label>
                        <select
                          value={formData.status}
                          onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value }))}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                          <option value="active">Activa</option>
                          <option value="inactive">Inactiva</option>
                        </select>
                      </div>
                    )}

                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Descripción
                      </label>
                      <textarea
                        value={formData.description}
                        onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                        rows={3}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Descripción de la plantilla..."
                      />
                    </div>
                  </div>

                  {/* Vista previa de estructura para edición */}
                  {selectedTemplate?.gdbStructure && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-3">
                        Estructura de la GeoDatabase
                      </label>
                      <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                        {(() => {
                          try {
                            logger.debug('Parsing GDB structure for edit template', { templateId: selectedTemplate?.id });
                            logger.debug('Raw gdbStructure', { gdbStructure: selectedTemplate?.gdbStructure });
                            let structure = selectedTemplate?.gdbStructure ?
                              (typeof selectedTemplate.gdbStructure === 'string' ?
                                JSON.parse(selectedTemplate.gdbStructure) :
                                selectedTemplate.gdbStructure) :
                              null;

                            // Normalize property names to lowercase for compatibility
                            if (structure) {
                              structure = {
                                featureClasses: (structure.FeatureClasses || structure.featureClasses || []).map(fc => ({
                                  name: fc.Name || fc.name,
                                  type: fc.Type || fc.type,
                                  spatialReferenceInfo: fc.SpatialReferenceInfo || fc.spatialReferenceInfo || null,
                                  fields: (fc.Fields || fc.fields || []).map(field => ({
                                    name: field.Name || field.name,
                                    type: field.Type || field.type,
                                    length: field.Length || field.length,
                                    required: field.Required || field.required
                                  })),
                                  validations: fc.Validations || fc.validations || []
                                })),
                                tables: (structure.Tables || structure.tables || []).map(table => ({
                                  name: table.Name || table.name,
                                  fields: (table.Fields || table.fields || []).map(field => ({
                                    name: field.Name || field.name,
                                    type: field.Type || field.type,
                                    length: field.Length || field.length,
                                    required: field.Required || field.required
                                  })),
                                  validations: table.Validations || table.validations || []
                                })),
                                domains: structure.Domains || structure.domains || [],
                                relationships: structure.Relationships || structure.relationships || []
                              };
                            }

                            logger.debug('Parsed structure for edit', { structure });
                            return <StructureViewer structure={structure} readOnly={true} />;
                          } catch (error) {
                            logger.error('Error parsing GDB structure for edit:', { error });
                            logger.debug('Raw gdbStructure content', { gdbStructure: selectedTemplate?.gdbStructure });
                            return (
                              <div className="text-center py-8">
                                <AlertCircle className="mx-auto text-red-400 mb-4" size={48} />
                                <h3 className="text-lg font-medium text-gray-900 mb-2">Error al cargar estructura</h3>
                                <p className="text-gray-600">No se pudo parsear la estructura de la GeoDatabase</p>
                                <details className="mt-4">
                                  <summary className="cursor-pointer text-sm text-gray-500">Ver detalles del error</summary>
                                  <div className="mt-2 text-xs bg-gray-100 p-2 rounded">
                                    <strong>Error:</strong> {error.message}
                                  </div>
                                  <div className="mt-2 text-xs bg-gray-100 p-2 rounded">
                                    <strong>Contenido raw:</strong>
                                    <pre className="mt-1 max-h-20 overflow-auto">{selectedTemplate?.gdbStructure || 'null'}</pre>
                                  </div>
                                </details>
                              </div>
                            );
                          }
                        })()}
                      </div>
                    </div>
                  )}

                  {/* Carga de archivo GDB - Solo para creación */}
                  {modalType === 'create' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-3">
                        Archivo GeoDatabase comprimido (.zip)
                      </label>
                      <div
                        className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
                          isDragging
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-gray-300 hover:border-gray-400'
                        }`}
                        onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                        onDragLeave={(e) => { e.preventDefault(); setIsDragging(false); }}
                        onDrop={handleDrop}
                      >
                        <Upload className="mx-auto text-gray-400 mb-4" size={48} />
                        <div className="space-y-2">
                          <p className="text-sm text-gray-600">
                            Arrastre y suelte su archivo .zip aquí, o
                          </p>
                          <label className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 cursor-pointer transition-colors">
                            <Upload className="mr-2" size={16} />
                            Seleccionar archivo
                            <input
                              type="file"
                              accept=".zip"
                              onChange={handleFileUpload}
                              className="hidden"
                            />
                          </label>
                        </div>
                        {formData.gdbFile && (
                          <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                            <div className="flex items-center justify-center text-green-800">
                              <CheckCircle className="mr-2" size={16} />
                              <span className="text-sm font-medium">
                                {formData.gdbFile.name}
                              </span>
                            </div>
                          </div>
                        )}
                        {formErrors.gdbFile && (
                          <p className="mt-2 text-sm text-red-600">{formErrors.gdbFile}</p>
                        )}
                      </div>
                    </div>
                  )}



                  {/* Botones del modal */}
                  <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
                    <BaseButton
                      type="button"
                      variant="secondary"
                      onClick={closeModal}
                    >
                      Cancelar
                    </BaseButton>
                    <BaseButton
                      type="submit"
                      loading={loading}
                      disabled={loading || (modalType === 'create' && !formData.gdbFile)}
                    >
                      {modalType === 'create' && 'Crear Plantilla'}
                      {modalType === 'edit' && 'Actualizar Plantilla'}
                      {modalType === 'clone' && 'Clonar Plantilla'}
                    </BaseButton>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Panel de Biblioteca */}
      {showLibraryPanel && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-start justify-end">
          <div className="bg-white h-full w-full max-w-3xl flex flex-col shadow-2xl">

            {/* Header del panel */}
            <div className="p-6 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-indigo-100 rounded-lg">
                  <BookOpen className="text-indigo-600" size={22} />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900">Biblioteca de Plantillas</h2>
                  <p className="text-sm text-gray-500">Plantillas oficiales disponibles para todos los usuarios</p>
                </div>
              </div>
              <button
                onClick={() => { setShowLibraryPanel(false); setLibraryCloneTarget(null); }}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                title="Cerrar"
              >
                <X size={20} />
              </button>
            </div>

            {/* Filtros */}
            <div className="p-4 border-b border-gray-100 flex-shrink-0">
              <div className="flex gap-3">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                  <input
                    type="text"
                    placeholder="Buscar en la Biblioteca..."
                    value={librarySearchTerm}
                    onChange={e => setLibrarySearchTerm(e.target.value)}
                    className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>
                <select
                  value={libraryCategoryFilter}
                  onChange={e => setLibraryCategoryFilter(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <option value="all">Todas las categorías</option>
                  {[...new Set(libraryTemplates.map(t => t.category).filter(Boolean))].map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
                <button
                  onClick={loadLibraryTemplates}
                  className="px-3 py-2 text-gray-500 hover:text-indigo-600 hover:bg-indigo-50 border border-gray-300 rounded-lg transition-colors"
                  title="Actualizar lista"
                >
                  <Search size={16} />
                </button>
              </div>
            </div>

            {/* Cuerpo del panel */}
            <div className="flex-1 overflow-y-auto p-4">
              {libraryLoading ? (
                <div className="flex justify-center py-16">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                </div>
              ) : (() => {
                const filtered = libraryTemplates.filter(t => {
                  const matchesSearch = !librarySearchTerm ||
                    (t.name?.toLowerCase() || '').includes(librarySearchTerm.toLowerCase()) ||
                    (t.description?.toLowerCase() || '').includes(librarySearchTerm.toLowerCase());
                  const matchesCategory = libraryCategoryFilter === 'all' || t.category === libraryCategoryFilter;
                  return matchesSearch && matchesCategory;
                });

                if (filtered.length === 0) {
                  return (
                    <div className="text-center py-16">
                      <BookOpen className="mx-auto text-gray-300 mb-4" size={56} />
                      <h3 className="text-lg font-medium text-gray-700 mb-1">
                        {libraryTemplates.length === 0 ? 'La Biblioteca está vacía' : 'Sin resultados'}
                      </h3>
                      <p className="text-sm text-gray-500">
                        {libraryTemplates.length === 0
                          ? 'Aún no hay plantillas publicadas en la Biblioteca.'
                          : 'Ninguna plantilla coincide con los filtros aplicados.'}
                      </p>
                    </div>
                  );
                }

                return (
                  <div className="space-y-3">
                    {filtered.map(template => (
                      <div
                        key={template.id}
                        className="border border-gray-200 rounded-lg p-4 hover:border-indigo-300 hover:bg-indigo-50 transition-colors"
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="font-semibold text-gray-900 truncate">{template.name}</span>
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-700 flex-shrink-0">
                                {template.category || 'Sin categoría'}
                              </span>
                            </div>
                            {template.description && (
                              <p className="text-sm text-gray-500 line-clamp-2 mb-2">{template.description}</p>
                            )}
                            <div className="flex items-center gap-4 text-xs text-gray-400">
                              <span className="flex items-center gap-1">
                                <FileText size={12} />
                                {template.validationRulesCount} {template.validationRulesCount === 1 ? 'regla' : 'reglas'}
                              </span>
                              {template.company && (
                                <span className="flex items-center gap-1">
                                  <User size={12} />
                                  {template.company}
                                </span>
                              )}
                            </div>
                          </div>

                          {libraryCloneTarget?.id === template.id ? (
                            // Mini-formulario de clonación inline
                            <div className="flex-shrink-0 w-64">
                              <input
                                type="text"
                                value={libraryCloneName}
                                onChange={e => { setLibraryCloneName(e.target.value); setLibraryCloneError(''); }}
                                placeholder="Nombre de tu copia..."
                                className="w-full px-3 py-1.5 text-sm border border-indigo-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 mb-1"
                                autoFocus
                                onKeyDown={e => e.key === 'Enter' && confirmCloneFromLibrary()}
                              />
                              {libraryCloneError && (
                                <p className="text-xs text-red-600 mb-1">{libraryCloneError}</p>
                              )}
                              <div className="flex gap-2">
                                <button
                                  onClick={confirmCloneFromLibrary}
                                  className="flex-1 px-3 py-1.5 text-xs font-medium bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
                                >
                                  Confirmar
                                </button>
                                <button
                                  onClick={() => { setLibraryCloneTarget(null); setLibraryCloneError(''); }}
                                  className="px-3 py-1.5 text-xs font-medium text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                                >
                                  Cancelar
                                </button>
                              </div>
                            </div>
                          ) : (
                            <button
                              onClick={() => handleCloneFromLibrary(template)}
                              className="flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-indigo-600 border border-indigo-300 rounded-lg hover:bg-indigo-600 hover:text-white transition-colors"
                              title="Clonar a mis plantillas"
                            >
                              <Copy size={14} />
                              Clonar
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })()}
            </div>

            {/* Footer del panel */}
            <div className="p-4 border-t border-gray-100 bg-gray-50 flex-shrink-0">
              <p className="text-xs text-gray-400 text-center">
                Las plantillas de la Biblioteca son de solo lectura. Clónalas para personalizarlas.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Confirmación de Eliminación */}
      {showDeleteModal && templateToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6 text-center">
              <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                <AlertTriangle className="h-6 w-6 text-red-600" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mt-4">
                Eliminar Plantilla
              </h3>
              <div className="mt-2">
                <p className="text-sm text-gray-500">
                  ¿Estás seguro de que quieres eliminar la plantilla <strong>{templateToDelete.name}</strong>?
                  Esta acción también eliminará todas las validaciones asociadas a la plantilla.
                  Esta acción no se puede deshacer.
                </p>
              </div>
            </div>
            <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
              <BaseButton
                variant="danger"
                onClick={confirmDelete}
                className="w-full sm:ml-3 sm:w-auto"
                disabled={loading}
              >
                {loading ? 'Eliminando...' : 'Eliminar'}
              </BaseButton>
              <BaseButton
                variant="outline"
                onClick={() => setShowDeleteModal(false)}
                className="mt-3 w-full sm:mt-0 sm:w-auto"
              >
                Cancelar
              </BaseButton>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TemplateManagementModule;